import React, { FC } from 'react';
import { setAutomationElement } from '@experian-uk/corvetteuk-common-ui';
import { BodyLarge400, TitleMedium500 } from '@nebula/global-styles';
import { getDeactivateAccountInfo, Product, PlanType } from '../../../../constants/deactivate';
import products from '../../../../constants/products';
import benefitsDocument from '../../../../constants/benefitsDocument';
import CalendarDeferredIcon from '../../../Icons/CalendarDeferredIcon';
import ImmediateCancelIcon from '../../../Icons/ImmediateCancelIcon';
import {
  Container,
  ButtonGroup,
  ChoiceButton,
  IconContainer,
  TextContainer,
  TimeLabel,
  EndDescription,
  StyledTitleLarge500,
  InfoTitle,
  StyledBodyLarge400,
  StyledTitleMedium500,
} from './index.styles';
import { List, BenefitsLink, BenefitsLinkText } from '../shared.styles';
import { ExternalLinkIcon } from '@nebula/icons/ui';
import usePageView from '../../../../hooks/usePageView';

interface ChoiceCancelStageProps {
  subscriptionFamily: string;
  productDisplayName: string;
  formattedNextBillingDate: string;
  product: Product;
  onSelectDeferred: () => void;
  onSelectImmediate: () => void;
  onMySubscriptionClick: () => void;
}

const ChoiceCancelStage: FC<ChoiceCancelStageProps> = ({
  subscriptionFamily,
  productDisplayName,
  formattedNextBillingDate,
  product,
  onSelectDeferred,
  onSelectImmediate,
  onMySubscriptionClick,
}) => {

  const PAGE_NAME = 'ProdMoveDeactivationTypePage';
  
  usePageView(PAGE_NAME);

  const isCreditExpert = subscriptionFamily === products.credit_expert;
  const isIdentityPlus = subscriptionFamily === products.identity;

  const deactivationInfo = getDeactivateAccountInfo({
    product,
    planType: PlanType.Paid,
    cancellationType: null,
    formattedBillingEndDate: formattedNextBillingDate,
    isCoolingOffEligible: true,
  }) || { title: '', helperTextList: [] as string[] };

  const CE_BENEFIT_LINK = `${benefitsDocument.baseUrl}/${benefitsDocument[products.credit_expert as keyof typeof benefitsDocument]}`;
  const ID_BENEFIT_LINK = `${benefitsDocument.baseUrl}/${benefitsDocument[products.identity as keyof typeof benefitsDocument]}`;

  return (
    <Container>
      <StyledTitleLarge500 as="h1">You&apos;re about to deactivate your account</StyledTitleLarge500>
      <StyledBodyLarge400>
        Go to{' '}
        <BenefitsLinkText role="button" tabIndex={0} onClick={onMySubscriptionClick}>
          My subscription
        </BenefitsLinkText>{' '}
        to cancel subscription and keep your account.
      </StyledBodyLarge400>
      <InfoTitle as="h2">{deactivationInfo.title.trim()}</InfoTitle>
      <List>
        {deactivationInfo.helperTextList.map((text, index) => (
          <li key={`${text}-${index}`}>
            <BodyLarge400>{text.trim()}</BodyLarge400>
          </li>
        ))}
      </List>
      {(isCreditExpert || isIdentityPlus) && (
        <BenefitsLink
          {...setAutomationElement('deactivate-benefits-link')}
          href={isCreditExpert ? CE_BENEFIT_LINK : ID_BENEFIT_LINK}
          target="_blank"
          rel="noreferrer"
        >
          See all the benefits of {productDisplayName}
          <ExternalLinkIcon iconColor="#255AF0" />
        </BenefitsLink>
      )}
      <StyledTitleMedium500>When do you want to deactivate your account?</StyledTitleMedium500>
      <ButtonGroup role="none">
        <ChoiceButton
          onClick={onSelectDeferred}
          aria-label={`Cancel on ${formattedNextBillingDate}. Your benefits stay active until then.`}
        >
          <IconContainer aria-hidden="true">
            <CalendarDeferredIcon />
          </IconContainer>
          <TextContainer aria-hidden="true">
            <TimeLabel>On {formattedNextBillingDate}</TimeLabel>
            <EndDescription>Your benefits stay active until then</EndDescription>
          </TextContainer>
        </ChoiceButton>
        <ChoiceButton
          onClick={onSelectImmediate}
          aria-label="Cancel now. Your benefits end today."
        >
          <IconContainer aria-hidden="true">
            <ImmediateCancelIcon />
          </IconContainer>
          <TextContainer aria-hidden="true">
            <TimeLabel>Now</TimeLabel>
            <EndDescription>Your benefits end today</EndDescription>
          </TextContainer>
        </ChoiceButton>
      </ButtonGroup>
    </Container>
  );
};

export default ChoiceCancelStage;
